// Comportements globaux, hors composants (mode app, service worker, Bootstrap,
// défilement, notifications push…), branchés sur les événements du routeur
// Inertia (`navigate` part aussi pour la page initiale).
//
// Un seul branchement par session de page : installGlobalBehaviours n'est
// appelé qu'une fois, chaque écouteur n'existe qu'en un exemplaire.
import { router } from '@inertiajs/react'
import type { Page } from '@inertiajs/core'
import { installProgressBar } from '@/app/progress'
import { locale as loadedLocale } from '@/lib/i18n'
import { csrfToken } from '@/lib/http'
import {
  isAppMode, isCapacitorNative, isInternalLink, isMobileOrTablet, openExternal, scrollIntoViewOnFocus,
  scrollToTop, isWebviewLayout, withWebviewParam,
} from '@/lib/webview'

type ShellPageProps = {
  i18n?: { locale?: string }
  webview?: boolean
  csrfToken?: string
  premiumUpgradePath?: string
  shell?: { bodyClass?: string; isLanding?: boolean } | null
  request?: { pageVisitKey?: string | null } | null
}

let installed = false
let lastVisit: { url: string; method: string } | null = null
let isLanding = false
const SERVER_BODY_CLASSES = ['webview-mode', 'is-landing', 'is-signed-in']

export function installGlobalBehaviours(): void {
  if (installed || typeof window === 'undefined') return
  installed = true

  installProgressBar()
  initialLoader()
  delegateTooltips()
  wireKeyboardScroll()
  wireAppModeLinks()
  initCapacitorNotifications()
  exposeWelcomeHandle()

  window.addEventListener('resize', applyMobileWebviewMode)
  saveScrollOnLeave()

  router.on('start', (event) => {
    const visit = event.detail.visit
    if (visit.prefetch) return
    lastVisit = { url: visit.url.href, method: String(visit.method).toLowerCase() }
    if (visit.async) return
    removeInitialLoader()
    closeOpenModals()
  })

  router.on('navigate', (event) => {
    const page = event.detail.page as Page<ShellPageProps>
    const props = page.props
    // Changement de langue : tout le document (lang, metas, textes serveur)
    // doit suivre ; c'est un rechargement complet.
    if (props.i18n?.locale && props.i18n.locale !== loadedLocale()) {
      window.location.reload()
      return
    }
    syncDocumentFromProps(props)
    if (healAppMode(props)) return
    initCapacitorNotifications()
    // Première page après une déconnexion (clearHistory) : les documents que
    // le service worker gardait pour le hors-ligne — HTML complet, props
    // comprises (portefeuille, réglages entreprise…) — partent avec
    // l'historique ; sinon, hors ligne, ils se rouvraient sans session.
    if (page.clearHistory) purgeCachedPages()
    stripConfirmedParam()
    cleanupModals()
    // Sur la landing (hors app), pas de remontée en haut : le retour arrière
    // garde sa position.
    if (!handleDeepLinkRedirects() && (!isLanding || isAppMode())) scrollToTop()
    markPageVisited(props.request?.pageVisitKey)
  })

  // Réponse non-Inertia (administration, téléchargement, page d'erreur
  // statique), au lieu de la fenêtre d'erreur d'Inertia : une visite GET
  // charge l'adresse visée en entier ; un envoi de formulaire affiche le HTML
  // reçu, comme l'aurait fait le navigateur. La réponse ne porte pas son URL :
  // on garde celle de la dernière visite.
  router.on('httpException', (event) => {
    const response = event.detail.response as { data?: unknown; headers?: Record<string, string | undefined> }
    // Page Inertia en 4xx (formulaire refusé, 422 avec les erreurs) : Inertia
    // l'affiche.
    if (response.headers?.['x-inertia'] !== undefined) return
    event.preventDefault()
    const target = lastVisit
    const data = response.data
    if (target && target.method !== 'get' && typeof data === 'string' && /<html|<!doctype/i.test(data)) {
      document.open()
      document.write(data)
      document.close()
      return
    }
    window.location.href = target?.url || window.location.href
  })
}

// ─── Service worker ─────────────────────────────────────────────────────────

// Cache des pages HTML du service worker (CACHE_PAGES, nv-pages-<version>,
// app/views/pwa/service_worker.js.erb : repli hors ligne).
function purgeCachedPages() {
  if (!('caches' in window)) return
  caches.keys()
    .then((keys) => Promise.all(keys.filter((key) => key.startsWith('nv-pages-')).map((key) => caches.delete(key))))
    .catch(() => {})
}

// ─── <html> / <body> ────────────────────────────────────────────────────────

function syncDocumentFromProps(props: ShellPageProps) {
  const body = document.body
  isLanding = !!props.shell?.isLanding
  const serverClasses = (props.shell?.bodyClass || '').split(/\s+/).filter(Boolean)
  for (const cls of SERVER_BODY_CLASSES) body.classList.toggle(cls, serverClasses.includes(cls))
  if (props.premiumUpgradePath) body.dataset.premiumUpgradePath = props.premiumUpgradePath
  const meta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
  const token = props.csrfToken || csrfToken()
  if (meta && token) meta.content = token
  applyMobileWebviewMode()
}

// Mise en page mobile (webview-mode) sur petit écran, jamais sur la landing.
function applyMobileWebviewMode() {
  const serverSet = document.body.dataset.serverWebview === 'true'
  if (isLanding) {
    // <html> garderait la classe posée par la page précédente : sur mobile,
    // webview_hide_elements.css masquerait alors l'en-tête de la landing.
    if (!serverSet && !isAppMode()) document.documentElement.classList.remove('webview-mode')
    return
  }
  if (isMobileOrTablet()) {
    document.body.classList.add('webview-mode')
    document.documentElement.classList.add('webview-mode')
  } else if (!serverSet && !isAppMode()) {
    document.body.classList.remove('webview-mode')
    document.documentElement.classList.remove('webview-mode')
  }
}

// ─── Chargeur initial (une fois par onglet, connecté, hors app) ─────────────

const INITIAL_LOAD_KEY = 'northvane_initial_load_complete'
const LOADER = { MIN_DURATION: 1200, MAX_DURATION: 2000, INTERVAL: 50, INCREMENT: 2, CAP: 95, FADE: 300 }

function removeInitialLoader() {
  document.getElementById('initial-loader')?.remove()
  document.body.setAttribute('data-initial-load', 'false')
}

function initialLoader() {
  if (sessionStorage.getItem(INITIAL_LOAD_KEY) === 'true') {
    removeInitialLoader()
    return
  }
  const element = document.getElementById('initial-loader')
  const bar = document.getElementById('initial-loader-progress')
  if (!element || !bar) {
    document.body.setAttribute('data-initial-load', 'false')
    return
  }
  element.classList.remove('hidden')
  const start = Date.now()
  let progress = 0
  let done = false
  const interval = window.setInterval(() => {
    if (progress < LOADER.CAP) {
      progress = Math.min(LOADER.CAP, progress + LOADER.INCREMENT)
      bar.style.width = `${progress}%`
    } else {
      window.clearInterval(interval)
    }
  }, LOADER.INTERVAL)

  const hide = () => {
    if (done) return
    done = true
    window.clearInterval(interval)
    window.clearTimeout(maxTimeout)
    sessionStorage.setItem(INITIAL_LOAD_KEY, 'true')
    element.classList.add('hidden')
    window.setTimeout(() => removeInitialLoader(), LOADER.FADE)
  }
  const maxTimeout = window.setTimeout(hide, LOADER.MAX_DURATION)
  const onLoad = () => {
    bar.style.width = '100%'
    window.setTimeout(hide, Math.max(0, LOADER.MIN_DURATION - (Date.now() - start)))
  }
  if (document.readyState === 'complete') onLoad()
  else window.addEventListener('load', onLoad, { once: true })
}

// ─── Bootstrap ──────────────────────────────────────────────────────────────

type BootstrapGlobal = {
  Tooltip: new (el: Element, opts: object) => unknown
  Modal: { getInstance(el: Element): { hide(): void } | null }
}
const bootstrapGlobal = () => (window as unknown as { bootstrap?: BootstrapGlobal }).bootstrap

// Infobulles : une instance déléguée sur <body>, active pour tout élément
// [data-bs-toggle="tooltip"] présent ou à venir.
function delegateTooltips() {
  const bs = bootstrapGlobal()
  if (!bs) {
    window.setTimeout(delegateTooltips, 50)
    return
  }
  try {
    new bs.Tooltip(document.body, { selector: '[data-bs-toggle="tooltip"]', delay: { show: 0, hide: 100 }, html: true })
  } catch { /* ignore */ }
}

function closeOpenModals() {
  const bs = bootstrapGlobal()
  document.querySelectorAll('.modal.show').forEach((modal) => bs?.Modal.getInstance(modal)?.hide())
  cleanupModals()
}

// Fonds de modale orphelins après une navigation.
function cleanupModals() {
  if (document.querySelector('.modal.show')) return
  document.querySelectorAll('.modal-backdrop').forEach((b) => b.remove())
  document.body.classList.remove('modal-open')
  document.body.style.removeProperty('overflow')
  document.body.style.removeProperty('padding-right')
}

// ─── Position de défilement au retour arrière ───────────────────────────────

// Inertia n'enregistre la position de la fenêtre qu'après 100 ms sans
// défilement : un clic juste après avoir défilé laisserait une position
// périmée, et le retour arrière ramènerait plus haut. On l'écrit donc dans
// l'entrée courante de l'historique au départ de chaque visite.
function saveScrollOnLeave() {
  document.addEventListener('inertia:before', (event) => {
    const visit = (event as CustomEvent).detail.visit as { prefetch?: boolean; async?: boolean; preserveScroll?: unknown; method?: string }
    if (visit.prefetch || visit.async || visit.preserveScroll === true || String(visit.method).toLowerCase() !== 'get') return
    const state = window.history.state as Record<string, unknown> | null
    if (!state || typeof state !== 'object' || !('page' in state)) return
    window.history.replaceState({ ...state, documentScrollPosition: { top: window.scrollY, left: window.scrollX } }, '')
  })
}

// ─── Mode app ───────────────────────────────────────────────────────────────

function wireKeyboardScroll() {
  document.addEventListener('focusin', (event) => {
    const target = event.target as HTMLElement | null
    if (!isWebviewLayout() || !target) return
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) scrollIntoViewOnFocus(target)
  }, true)
}

// Dans l'app : les liens externes (ou target=_blank) s'ouvrent dans le
// navigateur système ; les liens et formulaires internes reconduisent
// ?webview=true, que les anciennes versions de l'app (sans user-agent dédié)
// ne savent pas signaler autrement.
function wireAppModeLinks() {
  document.addEventListener('inertia:before', (event) => {
    if (!isAppMode()) return
    const visit = (event as CustomEvent).detail.visit as { url: URL }
    if (!visit.url.searchParams.has('webview') && !visit.url.searchParams.has('app')) visit.url.searchParams.set('webview', 'true')
  })

  document.addEventListener('click', (e) => {
    if (!isAppMode() || isLanding) return
    const link = (e.target as Element | null)?.closest?.('a')
    if (!link) return
    const href = link.getAttribute('href')
    if (!href || href.startsWith('javascript:') || href.startsWith('#')) return
    if (link.target === '_blank' || !isInternalLink(href)) {
      e.preventDefault()
      e.stopPropagation()
      openExternal(href.startsWith('http') ? href : new URL(href, window.location.origin).href)
    }
  }, true)

  // Lien interne ordinaire (pas un lien Inertia : HTML d'article rendu tel
  // quel, <a> simple) : ?webview=true est reconduit sur tout lien interne. En
  // phase de remontée, après React : un lien Inertia a déjà annulé l'action
  // par défaut, et inertia:before ajoute le paramètre à sa visite. Le
  // navigateur suit l'adresse complétée (chargement complet, comme ce même
  // lien hors de l'app).
  document.addEventListener('click', (e) => {
    if (!isAppMode() || isLanding || e.defaultPrevented) return
    const link = (e.target as Element | null)?.closest?.('a')
    const href = link?.getAttribute('href')
    if (!link || !href || href.startsWith('#') || href.startsWith('javascript:')) return
    if (link.target === '_blank' || !isInternalLink(href)) return
    const url = new URL(href, window.location.origin)
    if (url.origin !== window.location.origin || url.searchParams.has('webview') || url.searchParams.has('app')) return
    link.setAttribute('href', withWebviewParam(url.pathname + url.search + url.hash))
  })

  document.addEventListener('submit', (e) => {
    if (!isAppMode()) return
    const form = e.target as HTMLFormElement
    const action = form.getAttribute('action')
    if (!action || !isInternalLink(action) || action.includes('webview=true') || action.includes('app=true')) return
    if (!form.querySelector('input[name="webview"]')) {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'webview'
      input.value = 'true'
      form.appendChild(input)
    }
  }, true)
}

function handleDeepLinkRedirects(): boolean {
  const current = window.location.href
  if (!current.startsWith('northvane://')) return false
  const native = (window as unknown as { NorthvaneNative?: { logout?: () => void } }).NorthvaneNative
  const message = current.includes('account_deleted') ? 'account_deleted' : 'logged_out'
  if (current.includes('logout') || current.includes('signed_out') || current.includes('account_deleted')) {
    if (native?.logout) native.logout()
    else {
      localStorage.clear()
      sessionStorage.clear()
      window.location.href = `/auth.html?message=${message}`
    }
  }
  return true
}

// Ancienne app (sans user-agent dédié) : après une redirection du serveur qui
// a perdu ?webview=true, la page arrive rendue hors mode app alors que
// Capacitor signale une plateforme native. On la recharge avec le paramètre.
// Jamais dans un navigateur : seul Capacitor le déclenche, et jamais sur la
// landing.
function healAppMode(props: ShellPageProps): boolean {
  if (props.webview || isLanding || !isCapacitorNative()) return false
  const url = new URL(window.location.href)
  if (url.searchParams.has('webview') || url.searchParams.has('app')) return false
  url.searchParams.set('webview', 'true')
  window.location.replace(url.toString())
  return true
}

// ─── ?confirmed=true (première arrivée après confirmation du compte) ────────

function stripConfirmedParam() {
  const url = new URL(window.location.href)
  if (!url.searchParams.has('confirmed')) return
  window.dispatchEvent(new CustomEvent('northvane:account-confirmed'))
  url.searchParams.delete('confirmed')
  history.replaceState(history.state, '', url.pathname + url.search + url.hash)
}

// ─── Première visite d'une page (pastilles « nouveau » de la sidebar) ───────

// Une fois par clé et par document : `navigate` repart aussi sur les
// rechargements partiels, qui gardent les props `request` de la page.
const pagesMarkedVisited = new Set<string>()

function markPageVisited(key: string | null | undefined) {
  if (!key || pagesMarkedVisited.has(key)) return
  pagesMarkedVisited.add(key)
  fetch('/api/v1/onboarding/mark_feature_visited', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
    body: JSON.stringify({ page_key: key }),
  }).catch(() => {})
}

// ─── Console : NorthvaneWelcome.open() / close() / reset() ──────────────────

function exposeWelcomeHandle() {
  ;(window as unknown as { NorthvaneWelcome: object }).NorthvaneWelcome = {
    open() {
      if (document.getElementById('nv-welcome')) {
        window.dispatchEvent(new CustomEvent('northvane:welcome:open'))
        return 'opened'
      }
      const url = new URL(window.location.href)
      url.searchParams.set('welcome', '1')
      window.location.assign(url.toString())
      return 'reloading with ?welcome=1'
    },
    close() {
      window.dispatchEvent(new CustomEvent('northvane:welcome:close'))
      return 'closed'
    },
    async reset() {
      const response = await fetch('/api/v1/onboarding/welcome_reset', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
      })
      if (!response.ok) return `reset failed (${response.status}) — are you signed in?`
      return (this as { open(): string }).open()
    },
  }
}

// ─── Notifications push de l'app (Capacitor) ────────────────────────────────

const WEB_BASE = 'https://www.northvane.app'

function appendWebviewParam(url: string): string {
  try {
    const u = new URL(url)
    if (!u.searchParams.has('webview')) u.searchParams.set('webview', 'true')
    return u.toString()
  } catch {
    return url + (url.includes('?') ? '&' : '?') + 'webview=true'
  }
}

function resolveNotificationUrl(data: Record<string, string | undefined> | undefined): string {
  if (!data) return `${WEB_BASE}/dashboard?webview=true`
  let resolved: string
  if (data.url) {
    resolved = appendWebviewParam(data.url.startsWith('http') ? data.url : WEB_BASE + data.url)
  } else {
    const symbol = data.stock_symbol || data.symbol
    const enc = encodeURIComponent
    switch (data.type) {
      case 'blog':
      case 'blog_major_event':
      case 'breaking_article':
        resolved = data.blog_slug ? `${WEB_BASE}/blog/${enc(data.blog_slug)}?webview=true` : `${WEB_BASE}/blog?webview=true`
        break
      // Les deux sections du Radar marché (les anciennes adresses y redirigent).
      case 'geopolitical_alert':
      case 'macro_event':
        resolved = `${WEB_BASE}/radar?section=geopolitics&webview=true`
        break
      case 'social_sentiment':
        resolved = `${WEB_BASE}/radar?section=sentiment&webview=true`
        break
      case 'opportunity':
        resolved = `${WEB_BASE}/opportunities?webview=true`
        break
      case 'strong_signal':
      case 'pre_market_alert':
        resolved = symbol ? `${WEB_BASE}/stock-analysis/${enc(symbol)}?webview=true` : `${WEB_BASE}/dashboard?webview=true`
        break
      case 'simulation':
        resolved = data.simulation_id ? `${WEB_BASE}/simulations/${enc(data.simulation_id)}?webview=true` : `${WEB_BASE}/simulations?webview=true`
        break
      case 'graph_analysis':
        resolved = data.graph_analysis_id ? `${WEB_BASE}/graph_analyses/${enc(data.graph_analysis_id)}?webview=true` : `${WEB_BASE}/graph_analyses?webview=true`
        break
      case 'prediction_market':
        resolved = data.market_id ? `${WEB_BASE}/prediction_markets/${enc(data.market_id)}?webview=true` : `${WEB_BASE}/prediction_markets?webview=true`
        break
      default:
        resolved = `${WEB_BASE}/dashboard?webview=true`
    }
  }
  try {
    const host = new URL(resolved).hostname
    if (host !== 'www.northvane.app' && host !== 'northvane.app') return `${WEB_BASE}/dashboard?webview=true`
  } catch {
    return `${WEB_BASE}/dashboard?webview=true`
  }
  return resolved
}

function handleNotificationAction(event: { notification?: { data?: Record<string, string> }; data?: Record<string, string> }) {
  try {
    const url = resolveNotificationUrl(event?.notification?.data || event?.data || {})
    if (url && url !== window.location.href) window.location.href = url
  } catch (e) {
    console.error('[CapacitorNotif] Error handling notification tap:', e)
  }
}

function registerTokenWithServer(token?: string) {
  if (!token) return
  const cap = (window as unknown as { Capacitor: { getPlatform?: () => string } }).Capacitor
  const platform = cap.getPlatform?.() || 'ios'
  fetch(`${WEB_BASE}/api/v1/devices/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ device: { push_token: token, platform, app_version: '1.0.1' } }),
  }).catch((e) => console.error('[CapacitorNotif] Token registration failed:', e))
}

// Branchée une fois par document, dès qu'un des deux greffons est là.
// Capacitor.Plugins peut ne pas être prêt au démarrage : nouvel essai à
// chaque page et, en attendant la première navigation, quelques essais
// espacés.
let capacitorNotifInitialized = false
let capacitorNotifRetries = 0
let capacitorNotifTimer: number | null = null
const CAPACITOR_NOTIF_RETRY = { DELAY: 500, MAX: 10 }

function initCapacitorNotifications() {
  if (capacitorNotifInitialized) return
  const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean; Plugins?: Record<string, any> } }).Capacitor
  if (!cap?.isNativePlatform?.()) return
  const plugins = cap.Plugins
  if (!plugins?.FirebaseMessaging && !plugins?.PushNotifications) {
    if (capacitorNotifTimer === null && capacitorNotifRetries < CAPACITOR_NOTIF_RETRY.MAX) {
      capacitorNotifRetries++
      capacitorNotifTimer = window.setTimeout(() => {
        capacitorNotifTimer = null
        initCapacitorNotifications()
      }, CAPACITOR_NOTIF_RETRY.DELAY)
    }
    return
  }
  capacitorNotifInitialized = true
  try {
    if (plugins.FirebaseMessaging) {
      plugins.FirebaseMessaging.addListener('notificationActionPerformed', handleNotificationAction)
      plugins.FirebaseMessaging.addListener('tokenReceived', (e: { token: string }) => registerTokenWithServer(e.token))
      plugins.FirebaseMessaging.requestPermissions().then((result: { receive: string }) => {
        if (result.receive === 'granted') plugins.FirebaseMessaging.getToken().then((r: { token: string }) => registerTokenWithServer(r.token))
      })
      return
    }
    if (plugins.PushNotifications) {
      plugins.PushNotifications.addListener('pushNotificationActionPerformed', handleNotificationAction)
      plugins.PushNotifications.addListener('registration', (e: { value: string }) => registerTokenWithServer(e.value))
      plugins.PushNotifications.requestPermissions().then((result: { receive: string }) => {
        if (result.receive === 'granted') plugins.PushNotifications.register()
      })
    }
  } catch (e) {
    console.error('[CapacitorNotif] Failed to register notification listeners:', e)
    capacitorNotifInitialized = false
  }
}
